/** @jsxImportSource react */
import { useEffect } from 'react'
import { Alert, Box, Button, Paper, Typography } from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { SUBSCRIPTION_EVENT } from '../../api/crm'
import { useSubscription, useTenantKey } from '../../hooks/useCrmQueries'
import { daysUntil, plural } from '../../utils/billing'

const day = (iso) => (iso ? format(new Date(iso), 'd MMM yyyy') : '')

/** Members see the trial notice only near its end; admins all along (they choose the plan). */
const TRIAL_NOTICE_DAYS = 3

/**
 * Above every page: trial days left, an overdue renewal, read-only. Refreshes when a request is
 * refused because the subscription ended, so the banner appears without a reload.
 */
export const SubscriptionBanner = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const tenantId = useTenantKey()
  const { data: sub } = useSubscription()

  useEffect(() => {
    const refresh = () => queryClient.invalidateQueries({ queryKey: [tenantId, 'billing'] })
    window.addEventListener(SUBSCRIPTION_EVENT, refresh)
    return () => window.removeEventListener(SUBSCRIPTION_EVENT, refresh)
  }, [queryClient, tenantId])

  if (!sub?.billingEnabled) return null
  const days = daysUntil(sub.phaseEndsAt)
  const limit = sub.canManage && sub.phase !== 'READ_ONLY' && sub.phase !== 'LOCKED' ? nearLimit(sub) : null
  const action = sub.canManage && (
    <Button color="inherit" size="small" onClick={() => navigate('/settings/billing')} sx={{ whiteSpace: 'nowrap' }}>
      {sub.phase === 'TRIAL' ? 'Choose a plan' : limit && sub.phase === 'ACTIVE' ? 'Upgrade' : 'Billing'}
    </Button>
  )

  let notice = null
  if (sub.phase === 'TRIAL' && (sub.canManage || days <= TRIAL_NOTICE_DAYS)) {
    notice = {
      severity: days <= TRIAL_NOTICE_DAYS ? 'warning' : 'info',
      text: days === 0 ? 'Your free trial ends today.' : `Free trial: ${plural(days, 'day')} left (until ${day(sub.phaseEndsAt)}).`
        + (sub.canManage ? '' : ' Your admin can choose a plan.'),
    }
  } else if (sub.phase === 'GRACE') {
    notice = {
      severity: 'warning',
      text: `The subscription renewal is overdue. Renew by ${day(sub.phaseEndsAt)} to keep making changes.`
        + (sub.canManage ? '' : ' Please let your admin know.'),
    }
  } else if (sub.phase === 'READ_ONLY') {
    notice = {
      severity: 'error',
      text: `This workspace is read-only because its subscription ended. You can still view and export data; it locks on ${day(sub.phaseEndsAt)}.`
        + (sub.canManage ? '' : ' Your admin can renew it.'),
    }
  }
  if (!notice && limit) {
    notice = {
      severity: limit.full ? 'warning' : 'info',
      text: limit.full ? `This workspace has reached its ${limit.label} limit; nothing more can be added.`
        : `This workspace has used ${limit.percent}% of its ${limit.label}.`,
    }
  }
  if (!notice) return null
  return (
    <Alert severity={notice.severity} action={action} sx={{ mb: 2, alignItems: 'center' }}>
      {notice.text}
    </Alert>
  )
}

/** The fullest of seats, records and storage at 80 % or more (admins see usage), else null. */
const nearLimit = (sub) => {
  const u = sub.usage
  if (!u) return null
  const checks = [
    { label: 'members', used: u.members + u.openInvites, max: sub.limits.seats },
    { label: 'records', used: u.records, max: sub.limits.records },
    { label: 'file storage', used: u.storageBytes / 1024 / 1024, max: sub.limits.storageMb },
  ].filter((c) => c.max && c.used / c.max >= 0.8)
  if (!checks.length) return null
  const top = checks.reduce((a, b) => (b.used / b.max > a.used / a.max ? b : a))
  return { label: top.label, percent: Math.floor((top.used / top.max) * 100), full: top.used >= top.max }
}

/** Instead of the page while the workspace is locked (billing stays reachable for admins). */
export const LockedScreen = ({ sub }) => {
  const navigate = useNavigate()
  return (
    <Paper variant="outlined" sx={{ p: { xs: 3, md: 5 }, maxWidth: 560, mx: 'auto', mt: { xs: 2, md: 6 }, textAlign: 'center' }}>
      <Box sx={{ color: 'text.secondary', mb: 1 }}><LockOutlinedIcon sx={{ fontSize: 40 }} /></Box>
      <Typography variant="h5" sx={{ mb: 1 }}>This workspace is locked</Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Its subscription ended{sub.paidUntil ? ` on ${day(sub.paidUntil)}` : ''}. Nothing has been deleted: renewing brings everything back at once.
        {!sub.canManage && ' Ask an admin of this workspace to renew it.'}
      </Typography>
      {sub.canManage && <Button variant="contained" onClick={() => navigate('/settings/billing')}>Go to billing</Button>}
    </Paper>
  )
}
